import {useState, useContext} from 'react';
import {DraggableWithHandle} from '@cld/draggable';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {DndProvider} from 'react-dnd';
import {fill} from '@cloudinary/url-gen/actions/resize';
import {AdvancedImage} from '@cloudinary/react';
import {Item, ItemLayout} from '@cld/item';
import {AppContext} from './AppContext';
import {move} from '@cld/draggable';
import {TextField} from '@cld/text-field';
import {LinkButton, Button} from '@cld/button-next';
import {DropdownMenu} from '@cld/dropdown-menu';
import {MenuItem} from '@cld/menu';
import {MoreVert} from '@cld/icon-next';
import styled from 'styled-components';
import defaultTheme from '@cld/themes';
import {useDebouncedCallback} from 'use-debounce';
import {useToast} from "@shopify/app-bridge-react";

const ItemStyled = styled(Item)`
  cursor: default;
  flex: 1;
`;

const RightPartStyled = styled(ItemLayout)`
  width: 280px;

  > div {
    margin: 0;
  }
`;

const DraggableStyled = styled(DraggableWithHandle)`
  background-color: ${defaultTheme.palette.surface};
  border: 1px solid ${defaultTheme.palette.backgroundAlt};
  box-shadow: 0px 5px 5px -5px rgba(0, 0, 0, 0.24);

  svg path {
    fill: #6d7175;
  }

  &:not(:last-child) {
    border-bottom: none;
  }
`;

const Trimmed = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${(props) => (props.width ? props.width : '100%')};
  font-size: ${(props) =>
          props.size ? defaultTheme.font.size[props.size] : defaultTheme.font.size.xs};
`;

const LinkButtonStyled = styled(LinkButton)`
  color: ${defaultTheme.palette.primary};
  display: inline-block;
  padding: 0;
  height: auto;

  > div {
    color: inherit;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function CldMediaCards(props) {
    const {media, setMedia, productId, setMediaChanged, cld} = props;
    let {postBackendUrl} = useContext(AppContext);
    const {show} = useToast()


    function AltText(props) {
        let {alt, idx} = props;
        const [showEdit, setShowEdit] = useState(false);
        const [altText, setAltText] = useState(alt);
        const [origText] = useState(alt);

        function toggleEdit() {
            setShowEdit(!showEdit);
        }

        function handleCancelClick() {
            setAltText(origText);
            toggleEdit();
        }

        function handleSaveClick() {
            let md = media.map((m) => {
                if (m.position === idx) {
                    m.alt = altText;
                }
                return m;
            });
            setMedia(md);
            toggleEdit();
        }

        function handleChange(val) {
            setAltText(val);
        }

        return (
            <>
                {!showEdit && (
                    <>
                        <Trimmed size="xxs">{alt}</Trimmed>
                        <LinkButtonStyled onClick={toggleEdit}>Edit alt text</LinkButtonStyled>
                    </>
                )}
                {showEdit && (
                    <>
                        <TextField autoFocus  type={'text'} value={altText} onChange={handleChange}/>
                        <ButtonsWrapper>
                            <LinkButtonStyled onClick={handleSaveClick}>
                                Save alt text
                            </LinkButtonStyled>
                            <LinkButtonStyled onClick={handleCancelClick}>Cancel</LinkButtonStyled>
                        </ButtonsWrapper>
                    </>
                )}
            </>
        );
    }

    function ItemMenu(props) {
        const {id, mediaId} = props;

        async function deleteItem() {
            let req = {
                pid: productId,
                shopifyMediaId: mediaId
            }
            let res = await postBackendUrl('shopify/delete-media', req)
            if (!res.ok) {
                show('Error deleting media', {error: true})
            } else {
                if (res.media) {
                    setMediaChanged(false)
                    setMedia(res.media)
                }
            }
        }

        const items = (
            <MenuItem onClick={deleteItem} id={id}>
                <ItemLayout>Delete</ItemLayout>
            </MenuItem>
        );

        return (
            <>
                {mediaId && <DropdownMenu
                    maxWidth={'sm'}
                    minWidth={'sm'}
                    placement={'bottomRight'}
                    focusOnClose={false}
                    items={items}
                >
                    <Button id={id} variant="secondary" tone="plain" leftSlot={<MoreVert/>}/>
                </DropdownMenu>}
            </>
        );
    }

    const onMoveCallback = useDebouncedCallback((currentIdx, toIdx) => {
        let newMediaOrder = [];
        move(media, currentIdx, toIdx).forEach((itm, idx) => {
            if (itm) {
                itm.position = idx;
                newMediaOrder.push(itm);
            }
        });
        setMedia(newMediaOrder);
    }, 50);


    function RightPart(props) {
        const {public_id, alt, position, mediaId} = props;
        return (
            <RightPartStyled rightSlot={<ItemMenu id={public_id} mediaId={mediaId}/>}>
                <AltText alt={alt} idx={position}/>
            </RightPartStyled>
        );
    }

    function DragableMediaCards(props) {
        const {public_id, position, alt, width, height, format, shopify_id, type} = props.media;
        if (public_id) {
            let image;
            if (type.toLowerCase() === 'image') {
                image = cld.image(public_id);
            } else if (type.toLowerCase() === 'video') {
                image = image = cld.video(public_id);
            }
            const fileName = public_id.substring(public_id.lastIndexOf('/') + 1);
            image.resize(fill().width(50).height(50));
            image.format('jpg');
            image.quality('auto');
            const whfLine = `${width} x ${height}px, ${format}`;
            return (
                <DraggableStyled
                    id={fileName}
                    index={position}
                    type="cld-media"
                    onMove={onMoveCallback}
                    iconSize="lg"
                >
                    <ItemStyled className={'cld-media-card'} size={'lg'}>
                        <ItemLayout
                            leftSlot={<AdvancedImage cldImg={image}/>}
                            secondaryContent={whfLine}
                            rightSlot={
                                <RightPart public_id={public_id} position={position} alt={alt} mediaId={shopify_id}/>
                            }
                        >
                            <Trimmed size="xxs" width="320px">
                                {fileName}
                            </Trimmed>
                        </ItemLayout>
                    </ItemStyled>
                </DraggableStyled>
            );
        }
        return null;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            {media.map((m, idx) => (
                <DragableMediaCards key={m.public_id + '-' + idx} media={m}/>
            ))}
        </DndProvider>
    );
}
